// packages/shared/src/auth/twoFactor.types.ts
//
// Shared 2FA types + Zod schemas for the FE/BE boundary.
//
// Per CONTEXT.md
//   • 2FA enforcement on withdraw/transfer (members are blocked
//               BEFORE the 2FA gate — semantic block).
//   • Lock 15 min after 3 OTP fails inside a 5-min sliding window;
//               Email OTP resend cooldown 60 s; admin reset clears
//               `users.twoFactor.{secret, enabled, method}` and notifies the
//               user — they MUST re-setup before withdraw/transfer.
//   • TOTP-by-default with 8 single-use recovery codes.
//
// Node-pure surface — MUST NOT import Fastify, Mongoose, ioredis, otplib,
// or any Node-only API. Safe to bundle in Vite client builds.
import { z } from 'zod';
/** Zod-side enum (matches `TwoFactorMethod`). */
export const TwoFactorMethodSchema = z.enum(['totp', 'email']);
/** Zod-side enum mirroring `TwoFactorErrorCode`. */
export const TwoFactorErrorCodeSchema = z.enum([
    'TWO_FA_REQUIRED',
    'TWO_FA_LOCKED',
    'TWO_FA_RESEND_COOLDOWN',
    'TWO_FA_INVALID_CODE',
    'TWO_FA_NOT_CONFIGURED',
    'MEMBER_CANNOT_WITHDRAW',
]);
// ---------------------------------------------------------------------------
// Verify request / response — used by withdraw / transfer 2FA-step modals.
// Kept minimal so backend contract stays free to evolve; Zod stays the
// single source of truth on both sides.
// ---------------------------------------------------------------------------
/**
 * Wire shape for a 2FA verify call (e.g. `POST /auth/2fa/verify-step`).
 * Either `totpCode` (6 digits) or `emailOtpCode` (6 digits) is supplied —
 * NOT both. `recoveryCode` is the `xxxx-xxxx-xxxx` lowercase pattern.
 */
export const TwoFactorVerifyRequestSchema = z
    .object({
    method: TwoFactorMethodSchema.optional(),
    totpCode: z
        .string()
        .regex(/^\d{6}$/u, 'TOTP code must be 6 digits')
        .optional(),
    emailOtpCode: z
        .string()
        .regex(/^\d{6}$/u, 'Email OTP must be 6 digits')
        .optional(),
    recoveryCode: z
        .string()
        .regex(/^[a-z0-9]{4}-[a-z0-9]{4}-[a-z0-9]{4}$/u)
        .optional(),
})
    .superRefine((value, ctx) => {
    const provided = [
        value.totpCode,
        value.emailOtpCode,
        value.recoveryCode,
    ].filter((v) => typeof v === 'string' && v.length > 0).length;
    if (provided !== 1) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Exactly one of totpCode / emailOtpCode / recoveryCode must be provided',
        });
    }
});
/**
 * Successful verify envelope. `proofToken` is the opaque short-lived
 * server-issued token the FE attaches as the `2fa-proof` header on the
 * follow-up withdraw / transfer call (per `requireFresh2FA` middleware).
 */
export const TwoFactorVerifySuccessSchema = z.object({
    ok: z.literal(true),
    proofToken: z.string().min(1),
    expiresAt: z.string().datetime(),
});
/**
 * Failure envelope — uses the closed `TwoFactorErrorCode` set so FE can
 * exhaustively switch on `error.code`.
 */
export const TwoFactorVerifyFailureSchema = z.object({
    ok: z.literal(false),
    error: z.object({
        code: TwoFactorErrorCodeSchema,
        message: z.string(),
        retryAfterSeconds: z.number().int().nonnegative().optional(),
    }),
});
export const TwoFactorVerifyResponseSchema = z.discriminatedUnion('ok', [
    TwoFactorVerifySuccessSchema,
    TwoFactorVerifyFailureSchema,
]);
// ---------------------------------------------------------------------------
// Lock / cooldown shapes — surfaced to FE so it can render
// count-down UI without recomputing TTL math from `Retry-After`.
// ---------------------------------------------------------------------------
/** TTL of the lock + cooldown windows in seconds. */
export const TWO_FA_LOCK_TTL_SECONDS = 15 * 60; // 15 minutes
export const TWO_FA_FAIL_WINDOW_SECONDS = 5 * 60; // 5 minutes
export const TWO_FA_FAIL_THRESHOLD = 3;
export const TWO_FA_RESEND_COOLDOWN_SECONDS = 60;
//# sourceMappingURL=twoFactor.types.js.map